import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home as HomeIcon,
  Building,
  Globe2,
  Plane,
  PhoneCall,
  Clock,
  HeartHandshake,
  ArrowRight,
  Check,
  PlayCircle,
} from 'lucide-react';
import DynamicFormModal from '../components/Site/DynamicFormModal';
import IntroSequence from '../components/Site/dna/IntroSequence';
import './HomeDNA.css';

/**
 * Page d'accueil DNA-24 — direction claire & chaleureuse
 * (sable/ivoire + vert forêt profond + touches corail doux).
 * Structure pilotée par data (sections/cartes itérées) pour préparer
 * une future gestion CMS. Marque DNA uniquement (ASAR jamais impacté).
 */

const INTRO_SEEN_KEY = 'dnaIntroSeen';

const MEDIA = {
  hero: 'https://images.pexels.com/photos/24428847/pexels-photo-24428847.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600',
  careline: 'https://images.pexels.com/photos/7681579/pexels-photo-7681579.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1000',
  regions: 'https://images.unsplash.com/photo-1770064319970-9b9d140b7584?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxhZXJpYWwlMjBjaXR5JTIwc2t5bGluZSUyMGRheXRpbWUlMjBicmlnaHQlMjB3YXJtJTIwYXNpYXxlbnwwfHx8fDE3ODE4MzAzMDl8MA&ixlib=rb-4.1.0&q=85',
};

// --- Révélation au scroll (IntersectionObserver, sans dépendance) ----------
const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`dna-reveal ${shown ? 'is-visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// --- Sous-composants présentationnels (DRY) --------------------------------
const SectionLabel = ({ children, className = 'text-brand-accent' }) => (
  <span className={`inline-block uppercase text-xs sm:text-sm tracking-[0.25em] font-semibold mb-5 ${className}`}>
    {children}
  </span>
);

const PrimaryButton = ({ onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="group inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white font-medium px-8 py-4 rounded-full shadow-sm transition-all duration-300 hover:scale-[1.03] hover:shadow-lg"
  >
    {children}
    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" strokeWidth={2} />
  </button>
);

const ServiceCard = ({ id, Icon, title, description, more, delay }) => (
  <Reveal delay={delay}>
    <Link
      to={`/solutions/${id}`}
      data-testid={`dna-service-card-${id}`}
      className="group block h-full bg-white border border-brand-border rounded-2xl p-8 shadow-sm transition-all duration-500 hover:shadow-md hover:border-brand-primary/30 hover:-translate-y-1.5"
    >
      <div className="w-12 h-12 rounded-xl bg-brand-primary/10 flex items-center justify-center mb-6">
        <Icon className="w-6 h-6 text-brand-primary" strokeWidth={1.75} />
      </div>
      <h3 className="text-xl font-medium text-brand-heading mb-3">{title}</h3>
      <p className="text-brand-text-light text-sm leading-relaxed mb-6">{description}</p>
      <span className="inline-flex items-center gap-1 text-brand-primary text-sm font-medium">
        {more}
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
      </span>
    </Link>
  </Reveal>
);

// --- Page ------------------------------------------------------------------
const HomeDNA = () => {
  const { t } = useTranslation('public');
  const [selectedFormSlug, setSelectedFormSlug] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [introSound, setIntroSound] = useState(null);
  const [melting, setMelting] = useState(false);

  // Intro cinématique : jouée automatiquement à la 1ʳᵉ visite uniquement.
  // À la 1ʳᵉ visite, le son est décidé par le consentement cookies (initialSound=null → attend l'événement).
  // Si les cookies sont déjà acceptés (pas de bannière, donc pas de geste), on démarre en muet.
  useEffect(() => {
    if (!window.localStorage.getItem(INTRO_SEEN_KEY)) {
      const cookiesPending = !window.localStorage.getItem('asar_cookie_consent');
      setIntroSound(cookiesPending ? null : false);
      setIntroOpen(true);
    }
  }, []);

  const closeIntro = () => {
    window.localStorage.setItem(INTRO_SEEN_KEY, '1');
    setIntroOpen(false);
    setMelting(false);
  };

  const openStudyForm = () => setSelectedFormSlug('demande-generale');

  // Données pilotant les sections (prêtes pour une future gestion CMS).
  const services = [
    { id: 'particuliers', Icon: HomeIcon, title: t('home.solutions.individuals.title'), description: t('home.solutions.individuals.description') },
    { id: 'professionnels', Icon: Building, title: t('home.solutions.professionals.title'), description: t('home.solutions.professionals.description') },
    { id: 'expatries', Icon: Globe2, title: t('home.solutions.expatriates.title'), description: t('home.solutions.expatriates.description') },
    { id: 'voyage', Icon: Plane, title: t('home.solutions.travel.title'), description: t('home.solutions.travel.description') },
  ];

  const carelinePoints = [t('home.careline.point1'), t('home.careline.point2'), t('home.careline.point3')];

  const stats = [
    { value: t('home.whyUs.stat1Value'), label: t('home.whyUs.stat1Label') },
    { value: t('home.whyUs.stat2Value'), label: t('home.whyUs.stat2Label') },
    { value: t('home.whyUs.stat3Value'), label: t('home.whyUs.stat3Label') },
  ];

  return (
    <div className="dna-home-modern text-brand-text" data-testid="dna-home">
      {introOpen && <IntroSequence onClose={closeIntro} initialSound={introSound} onStart={() => setMelting(true)} />}

      {/* Filtre de transition « fonte/coulure » appliqué à la home quand l'intro démarre. */}
      {melting && (
        <svg className="dna-melt-svg" aria-hidden="true">
          <defs>
            <filter id="dna-melt">
              <feTurbulence type="fractalNoise" baseFrequency="0.012 0.018" numOctaves="2" result="noise">
                <animate attributeName="baseFrequency" dur="1.6s" values="0.012 0.018; 0.02 0.05; 0.012 0.03" fill="freeze" />
              </feTurbulence>
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="0" xChannelSelector="R" yChannelSelector="G">
                <animate attributeName="scale" dur="1.6s" values="0; 50; 140" fill="freeze" />
              </feDisplacementMap>
            </filter>
          </defs>
        </svg>
      )}

      <div className={melting ? 'dna-home-melt' : ''}>
      {/* ============================ HERO ============================ */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="dna-hero">
        <div className="absolute inset-0">
          <img src={MEDIA.hero} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-ivory via-brand-ivory/80 to-brand-ivory/10" />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-ivory/40 via-transparent to-brand-ivory" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-20">
          <div className="max-w-3xl">
            <div className="dna-hero-in" style={{ animationDelay: '0.1s' }}>
              <span
                data-testid="dna-hero-badge"
                className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-brand-border text-brand-primary text-sm font-medium px-4 py-2 rounded-full shadow-sm mb-7"
              >
                <Clock className="w-4 h-4" strokeWidth={2} />
                {t('home.hero.badge')}
              </span>
            </div>
            <h1
              className="dna-hero-in text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-brand-heading mb-6"
              style={{ animationDelay: '0.25s' }}
            >
              {t('home.hero.title')}
            </h1>
            <p className="dna-hero-in text-lg md:text-xl text-brand-primary font-medium mb-4" style={{ animationDelay: '0.4s' }}>
              {t('home.hero.subtitle')}
            </p>
            <p className="dna-hero-in text-base md:text-lg text-brand-text-light leading-relaxed mb-9 max-w-xl" style={{ animationDelay: '0.5s' }}>
              {t('home.hero.description')}
            </p>
            <div className="dna-hero-in flex flex-col sm:flex-row gap-4" style={{ animationDelay: '0.65s' }}>
              <PrimaryButton onClick={openStudyForm} testid="dna-hero-cta-primary">
                {t('common.requestStudy')}
              </PrimaryButton>
              <Link
                to="/contact"
                data-testid="dna-hero-cta-secondary"
                className="inline-flex items-center justify-center bg-white/70 text-brand-primary border border-brand-primary font-medium px-8 py-4 rounded-full transition-all duration-300 hover:bg-brand-primary/5"
              >
                {t('common.contactUs')}
              </Link>
            </div>
            <button
              type="button"
              onClick={() => {
                setIntroSound(true);
                setIntroOpen(true);
              }}
              data-testid="dna-replay-intro"
              className="dna-hero-in mt-8 inline-flex items-center gap-2 text-brand-text-light hover:text-brand-primary text-sm font-medium transition-colors"
              style={{ animationDelay: '0.8s' }}
            >
              <PlayCircle className="w-5 h-5" strokeWidth={1.75} />
              {t('home.replayIntro')}
            </button>
          </div>
        </div>
      </section>

      {/* ============================ SERVICES ============================ */}
      <section className="relative py-24 sm:py-32 bg-brand-ivory" data-testid="dna-services">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <SectionLabel>{t('home.solutions.title')}</SectionLabel>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-heading mb-5">
              {t('home.solutions.title')}
            </h2>
            <p className="text-base md:text-lg text-brand-text-light leading-relaxed">{t('home.solutions.subtitle')}</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <ServiceCard key={s.id} {...s} more={t('common.seeMore')} delay={i * 120} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================ CARELINE 24/7 ============================ */}
      <section className="relative py-24 sm:py-32 bg-brand-mint/40" data-testid="dna-careline">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 items-center gap-10 lg:gap-16">
            <Reveal className="lg:col-span-2">
              <div className="relative rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={MEDIA.careline}
                  alt={t('home.careline.title')}
                  data-testid="dna-careline-image"
                  className="w-full h-80 lg:h-[30rem] object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal className="lg:col-span-3" delay={120}>
              <span className="inline-flex items-center gap-2 bg-brand-primary/10 text-brand-primary text-sm font-medium px-4 py-2 rounded-full mb-6">
                <PhoneCall className="w-4 h-4" strokeWidth={2} />
                {t('home.careline.badge')}
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-heading mb-6">
                {t('home.careline.title')}
              </h2>
              <p className="text-base md:text-lg text-brand-text-light leading-relaxed mb-8 max-w-xl">
                {t('home.careline.description')}
              </p>
              <ul className="space-y-4">
                {carelinePoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-4 text-brand-text">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3.5 h-3.5 text-brand-primary" strokeWidth={3} />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============================ POURQUOI DNA-24 (STATS) ============================ */}
      <section className="relative py-24 sm:py-32 bg-brand-ivory" data-testid="dna-stats">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <SectionLabel>{t('home.whyUs.title')}</SectionLabel>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-heading mb-5">
              {t('home.whyUs.title')}
            </h2>
            <p className="text-base md:text-lg text-brand-text-light leading-relaxed">{t('home.whyUs.subtitle')}</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {stats.map((stat, i) => (
              <Reveal key={i} delay={i * 140} className="text-center">
                <div className="text-6xl md:text-7xl font-semibold tracking-tight mb-3 bg-clip-text text-transparent bg-gradient-to-b from-brand-primary to-brand-primary/40">
                  {stat.value}
                </div>
                <p className="text-brand-text-light text-base">{stat.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ RÉGIONS COUVERTES ============================ */}
      <section className="relative py-32 md:py-44 overflow-hidden" data-testid="dna-regions">
        <img
          src={MEDIA.regions}
          alt={t('home.regions.title')}
          data-testid="dna-regions-image"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-brand-primary/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ivory/20 via-transparent to-brand-ivory/30" />
        <Reveal className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <SectionLabel className="text-white/80">{t('home.hero.badge')}</SectionLabel>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-5 drop-shadow">
            {t('home.regions.title')}
          </h2>
          <p className="text-base md:text-lg text-white/90 leading-relaxed drop-shadow">{t('home.regions.subtitle')}</p>
        </Reveal>
      </section>

      {/* ============================ CTA FINALE ============================ */}
      <section className="relative py-28 sm:py-36 overflow-hidden bg-brand-ivory" data-testid="dna-final-cta">
        <div className="dna-glow-radial absolute inset-0" />
        <Reveal className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <HeartHandshake className="w-12 h-12 text-brand-accent mx-auto mb-7" strokeWidth={1.25} />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-heading mb-5">
            {t('home.finalCta.title')}
          </h2>
          <p className="text-base md:text-lg text-brand-text-light leading-relaxed mb-10">{t('home.finalCta.subtitle')}</p>
          <div className="flex justify-center">
            <PrimaryButton onClick={openStudyForm} testid="dna-final-cta-button">
              {t('home.finalCta.button')}
            </PrimaryButton>
          </div>
        </Reveal>
      </section>

      </div>

      {selectedFormSlug && (
        <DynamicFormModal
          slug={selectedFormSlug}
          onClose={() => setSelectedFormSlug(null)}
          backendUrl={process.env.REACT_APP_BACKEND_URL}
        />
      )}
    </div>
  );
};

export default HomeDNA;
